import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import {
  AbsenceId,
  HolidayId,
  SchedulePatternId,
  ShiftAssignmentId,
  ShiftTemplateId,
  ShiftTemplateVersionId,
  SiteId,
  WorkerId,
  WorkerSchedulePatternId,
} from '../common/ids';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import {
  AbsenceTypeSchema,
  DayPlanKindSchema,
  localDayDiff,
  PATTERN_APPLY_MAX_WORKERS,
  PATTERN_MAX_CYCLE_DAYS,
  SCHEDULE_GRID_MAX_CELLS,
  SCHEDULE_GRID_MAX_DAYS,
  ScheduleSourceSchema,
  SHIFT_MAX_DURATION_MINUTES,
  SHIFT_MIN_DURATION_MINUTES,
  shiftDurationMinutes,
  ShiftSummarySchema,
} from '../common/schedules';

import { LocalDateSchema, LocalTimeSchema, MarkAdminRefSchema } from './attendance';

/**
 * 008 · horarios, realm admin (contracts/admin-api.md §1–§5): plantillas de turno versionadas,
 * patrones, aplicación de patrones, cuadro de turnos (append-only) y feriados.
 *
 * Permisos: `schedules.read` para leer, `schedules.write` para el cuadro y las aplicaciones,
 * `schedules.configure` para plantillas, patrones y feriados. Los nombres de admins (`created_by`,
 * `archived_by`) salen de `schedule_admin_refs` (0009), nunca de un `JOIN admin_users`.
 * Parámetros desconocidos se descartan (objetos Zod no estrictos).
 */

// ---------------------------------------------------------------------------
// Referencias compartidas
// ---------------------------------------------------------------------------

/** Administrador que hizo algo (`{ id, name }`), resuelto por `schedule_admin_refs`. */
export const ScheduleAdminRefSchema = MarkAdminRefSchema;
export type ScheduleAdminRef = z.infer<typeof ScheduleAdminRefSchema>;

/** Trabajador en listados de horarios. */
export const ScheduleWorkerRefSchema = z.object({ id: WorkerId, name: z.string() });
export type ScheduleWorkerRef = z.infer<typeof ScheduleWorkerRefSchema>;

/** Sede en listados de horarios. */
export const ScheduleSiteRefSchema = z.object({ id: SiteId, name: z.string() });
export type ScheduleSiteRef = z.infer<typeof ScheduleSiteRefSchema>;

/** Filtro de estado de catálogos (plantillas y patrones). */
export const CatalogStatusFilterSchema = z.enum(['ACTIVE', 'ARCHIVED', 'ALL']);
export type CatalogStatusFilter = z.infer<typeof CatalogStatusFilterSchema>;

/** Refine de rango de días `from_day ≤ to_day` y `to − from + 1 ≤ maxDays`, con issue en `to_day`. */
export function refineDayRange(maxDays: number, fromKey = 'from_day', toKey = 'to_day') {
  return (value: Record<string, unknown>, ctx: z.core.$RefinementCtx) => {
    const from = value[fromKey];
    const to = value[toKey];
    if (typeof from !== 'string' || typeof to !== 'string') return;
    const diff = localDayDiff(from, to);
    if (diff < 0) {
      ctx.addIssue({
        code: 'custom',
        path: [toKey],
        message: 'La fecha final no puede ser anterior a la inicial',
      });
    } else if (diff + 1 > maxDays) {
      ctx.addIssue({
        code: 'custom',
        path: [toKey],
        message: `El período puede tener como máximo ${maxDays} días`,
      });
    }
  };
}

// ---------------------------------------------------------------------------
// §1 · Plantillas de turno
// ---------------------------------------------------------------------------

export const ShiftTemplateStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export type ShiftTemplateStatus = z.infer<typeof ShiftTemplateStatusSchema>;

/** Nombre de plantilla: 1–60 tras `trim` (único activo por empresa, sin distinguir mayúsculas). */
export const ShiftTemplateNameSchema = z.string().trim().min(1).max(60);

/** Código corto: 1–4 letras o dígitos; se normaliza a mayúsculas (único activo por empresa). */
export const ShiftTemplateCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9]{1,4}$/, { message: 'Usa de 1 a 4 letras o números' })
  .toUpperCase();

const shiftTimesShape = {
  start_time: LocalTimeSchema,
  end_time: LocalTimeSchema,
  break_minutes: z.number().int().min(0).max(180).default(0),
  /** `null` ⇒ rige la tolerancia de la empresa. */
  late_tolerance_minutes: z.number().int().min(0).max(60).nullable().default(null),
  early_leave_tolerance_minutes: z.number().int().min(0).max(60).nullable().default(null),
};

/**
 * Reglas de horas de un turno, con issue por campo: inicio ≠ fin; duración 60–1439 min (fin ≤
 * inicio ⇒ cruza medianoche); refrigerio menor que la duración.
 */
function refineShiftTimes(
  value: { start_time: string; end_time: string; break_minutes?: number },
  ctx: z.core.$RefinementCtx,
): void {
  if (value.start_time === value.end_time) {
    ctx.addIssue({
      code: 'custom',
      path: ['end_time'],
      message: 'La hora de fin debe ser distinta de la de inicio',
    });
    return;
  }
  const duration = shiftDurationMinutes(value.start_time, value.end_time);
  if (duration < SHIFT_MIN_DURATION_MINUTES || duration > SHIFT_MAX_DURATION_MINUTES) {
    ctx.addIssue({
      code: 'custom',
      path: ['end_time'],
      message: 'El turno debe durar entre 1 hora y 23 horas 59 minutos',
    });
    return;
  }
  if ((value.break_minutes ?? 0) >= duration) {
    ctx.addIssue({
      code: 'custom',
      path: ['break_minutes'],
      message: 'El refrigerio debe ser menor que la duración del turno',
    });
  }
}

/** Horas de un turno (compartidas por crear plantilla y crear versión). */
export const ShiftTimesBodySchema = z.object(shiftTimesShape).superRefine(refineShiftTimes);
export type ShiftTimesBody = z.infer<typeof ShiftTimesBodySchema>;

/** `POST /shift-templates`. */
export const CreateShiftTemplateBodySchema = z
  .object({ name: ShiftTemplateNameSchema, code: ShiftTemplateCodeSchema, ...shiftTimesShape })
  .superRefine(refineShiftTimes);
export type CreateShiftTemplateBody = z.infer<typeof CreateShiftTemplateBodySchema>;

/** `POST /shift-templates/{id}/versions`. `effective_from` ≥ primer día abierto (servidor). */
export const CreateShiftTemplateVersionBodySchema = z
  .object({ ...shiftTimesShape, effective_from: LocalDateSchema })
  .superRefine(refineShiftTimes);
export type CreateShiftTemplateVersionBody = z.infer<typeof CreateShiftTemplateVersionBodySchema>;

/** `PATCH /shift-templates/{id}`: nombre y código (sin cambio real ⇒ 200 sin auditoría). */
export const UpdateShiftTemplateBodySchema = z.object({
  name: ShiftTemplateNameSchema.optional(),
  code: ShiftTemplateCodeSchema.optional(),
});
export type UpdateShiftTemplateBody = z.infer<typeof UpdateShiftTemplateBodySchema>;

/** `GET /shift-templates?status`. */
export const ShiftTemplateListQuerySchema = z.object({
  status: CatalogStatusFilterSchema.default('ACTIVE'),
});
export type ShiftTemplateListQuery = z.infer<typeof ShiftTemplateListQuerySchema>;

export const ShiftTemplateIdParamsSchema = z.object({ id: ShiftTemplateId });
export type ShiftTemplateIdParams = z.infer<typeof ShiftTemplateIdParamsSchema>;

/** Versión de una plantilla (append-only). `effective_from = null` ⇒ la v1, sin vigencia. */
export const ShiftTemplateVersionSchema = z.object({
  id: ShiftTemplateVersionId,
  version: z.number().int().positive(),
  effective_from: LocalDateSchema.nullable(),
  start_time: LocalTimeSchema,
  end_time: LocalTimeSchema,
  crosses_midnight: z.boolean(),
  duration_minutes: z
    .number()
    .int()
    .min(SHIFT_MIN_DURATION_MINUTES)
    .max(SHIFT_MAX_DURATION_MINUTES),
  break_minutes: z.number().int().min(0).max(180),
  late_tolerance_minutes: z.number().int().min(0).max(60).nullable(),
  early_leave_tolerance_minutes: z.number().int().min(0).max(60).nullable(),
  created_at: IsoDateTimeSchema,
  created_by: ScheduleAdminRefSchema.nullable(),
});
export type ShiftTemplateVersion = z.infer<typeof ShiftTemplateVersionSchema>;

/** Plantilla con su versión vigente hoy. */
export const ShiftTemplateSchema = z.object({
  id: ShiftTemplateId,
  name: z.string(),
  code: z.string(),
  status: ShiftTemplateStatusSchema,
  source: ScheduleSourceSchema,
  external_ref: z.string().nullable(),
  current: ShiftTemplateVersionSchema,
  duration_minutes: z.number().int(),
  crosses_midnight: z.boolean(),
  /** Tiene asignaciones o patrones que la usan. */
  in_use: z.boolean(),
});
export type ShiftTemplate = z.infer<typeof ShiftTemplateSchema>;

/** `200/201` de lectura, creación, edición, versión y archivo. */
export const ShiftTemplateDetailSchema = ShiftTemplateSchema.extend({
  /** Descendente por `version`. */
  versions: z.array(ShiftTemplateVersionSchema),
  archived_at: NullableIsoDateTimeSchema,
  archived_by: ScheduleAdminRefSchema.nullable(),
});
export type ShiftTemplateDetail = z.infer<typeof ShiftTemplateDetailSchema>;

/** `GET /shift-templates` → `{ items }` (≤ 200, orden `code`). */
export const ShiftTemplateListResponseSchema = z.object({
  items: z.array(ShiftTemplateSchema).max(200),
});
export type ShiftTemplateListResponse = z.infer<typeof ShiftTemplateListResponseSchema>;

// ---------------------------------------------------------------------------
// §2 · Patrones
// ---------------------------------------------------------------------------

export const SchedulePatternKindSchema = z.enum(['WEEKLY', 'CYCLE']);
export type SchedulePatternKind = z.infer<typeof SchedulePatternKindSchema>;

export const SchedulePatternStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export type SchedulePatternStatus = z.infer<typeof SchedulePatternStatusSchema>;

export const PatternDayKindSchema = z.enum(['SHIFT', 'REST']);
export type PatternDayKind = z.infer<typeof PatternDayKindSchema>;

/** Un día del ciclo: `REST` sin plantillas; `SHIFT` con 1 o 2. */
export const PatternDaySchema = z
  .object({
    day_index: z
      .number()
      .int()
      .min(0)
      .max(PATTERN_MAX_CYCLE_DAYS - 1),
    kind: PatternDayKindSchema,
    template_ids: z.array(ShiftTemplateId).max(2),
  })
  .superRefine((day, ctx) => {
    if (day.kind === 'REST' && day.template_ids.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['template_ids'],
        message: 'Un día de descanso no lleva turnos',
      });
    }
    if (day.kind === 'SHIFT' && day.template_ids.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['template_ids'], message: 'Elige al menos un turno' });
    }
    if (new Set(day.template_ids).size !== day.template_ids.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['template_ids'],
        message: 'No repitas el mismo turno en un día',
      });
    }
  });
export type PatternDay = z.infer<typeof PatternDaySchema>;

/** Días exactamente `0..cycle_days − 1`, sin repetir. */
function refinePatternDays(
  days: readonly { day_index: number }[],
  cycleDays: number,
  ctx: z.core.$RefinementCtx,
): void {
  const indexes = days.map((d) => d.day_index).sort((a, b) => a - b);
  const expected = Array.from({ length: cycleDays }, (_, i) => i);
  if (indexes.length !== cycleDays || indexes.some((value, i) => value !== expected[i])) {
    ctx.addIssue({
      code: 'custom',
      path: ['days'],
      message: `El patrón debe definir exactamente ${cycleDays} días`,
    });
  }
}

/** `POST /schedule-patterns`. `WEEKLY` ⇒ `cycle_days = 7` (el día 0 es lunes). */
export const SchedulePatternBodySchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    kind: SchedulePatternKindSchema,
    cycle_days: z.number().int().min(1).max(PATTERN_MAX_CYCLE_DAYS),
    days: z.array(PatternDaySchema).min(1).max(PATTERN_MAX_CYCLE_DAYS),
  })
  .superRefine((body, ctx) => {
    if (body.kind === 'WEEKLY' && body.cycle_days !== 7) {
      ctx.addIssue({
        code: 'custom',
        path: ['cycle_days'],
        message: 'Un patrón semanal tiene 7 días',
      });
      return;
    }
    refinePatternDays(body.days, body.cycle_days, ctx);
  });
export type SchedulePatternBody = z.infer<typeof SchedulePatternBodySchema>;

/**
 * `PATCH /schedule-patterns/{id}`: nombre y/o días. `kind` y `cycle_days` son inmutables: el
 * servidor valida que `days` cubra exactamente el ciclo existente.
 */
export const UpdateSchedulePatternBodySchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  days: z.array(PatternDaySchema).min(1).max(PATTERN_MAX_CYCLE_DAYS).optional(),
});
export type UpdateSchedulePatternBody = z.infer<typeof UpdateSchedulePatternBodySchema>;

/** `GET /schedule-patterns?status`. */
export const SchedulePatternListQuerySchema = z.object({
  status: CatalogStatusFilterSchema.default('ACTIVE'),
});
export type SchedulePatternListQuery = z.infer<typeof SchedulePatternListQuerySchema>;

export const SchedulePatternIdParamsSchema = z.object({ id: SchedulePatternId });
export type SchedulePatternIdParams = z.infer<typeof SchedulePatternIdParamsSchema>;

/** Día del patrón con sus turnos resueltos (versión vigente hoy de cada plantilla). */
export const SchedulePatternDayViewSchema = z.object({
  day_index: z.number().int().nonnegative(),
  kind: PatternDayKindSchema,
  template_ids: z.array(ShiftTemplateId),
  shifts: z.array(ShiftSummarySchema),
});
export type SchedulePatternDayView = z.infer<typeof SchedulePatternDayViewSchema>;

export const SchedulePatternSchema = z.object({
  id: SchedulePatternId,
  name: z.string(),
  kind: SchedulePatternKindSchema,
  cycle_days: z.number().int().min(1).max(PATTERN_MAX_CYCLE_DAYS),
  status: SchedulePatternStatusSchema,
  source: ScheduleSourceSchema,
  external_ref: z.string().nullable(),
  days: z.array(SchedulePatternDayViewSchema),
});
export type SchedulePattern = z.infer<typeof SchedulePatternSchema>;

/** `GET /schedule-patterns/{id}`. */
export const SchedulePatternDetailSchema = SchedulePatternSchema.extend({
  active_applications: z.number().int().nonnegative(),
});
export type SchedulePatternDetail = z.infer<typeof SchedulePatternDetailSchema>;

export const SchedulePatternListResponseSchema = z.object({
  items: z.array(SchedulePatternSchema),
});
export type SchedulePatternListResponse = z.infer<typeof SchedulePatternListResponseSchema>;

// ---------------------------------------------------------------------------
// §3 · Aplicación de patrones
// ---------------------------------------------------------------------------

export const WorkerSchedulePatternStatusSchema = z.enum(['ACTIVE', 'ENDED']);
export type WorkerSchedulePatternStatus = z.infer<typeof WorkerSchedulePatternStatusSchema>;

export const WorkerSchedulePatternEndReasonSchema = z.enum([
  'REPLACED',
  'ADMIN',
  'WORKER_DEACTIVATED',
]);
export type WorkerSchedulePatternEndReason = z.infer<typeof WorkerSchedulePatternEndReasonSchema>;

/** `POST /schedule-pattern-assignments` (`?dry_run=true` no escribe). */
export const ApplyPatternBodySchema = z
  .object({
    pattern_id: SchedulePatternId,
    start_day: LocalDateSchema,
    end_day: LocalDateSchema.nullable().optional(),
    /** Defecto: la sede principal de cada trabajador. */
    site_id: SiteId.optional(),
    workers: z
      .array(
        z.object({
          worker_id: WorkerId,
          /** `< cycle_days` del patrón (servidor). */
          cycle_offset: z
            .number()
            .int()
            .min(0)
            .max(PATTERN_MAX_CYCLE_DAYS - 1)
            .default(0),
        }),
      )
      .min(1)
      .max(PATTERN_APPLY_MAX_WORKERS),
    replace_manual: z.boolean().default(false),
  })
  .superRefine((body, ctx) => {
    if (body.end_day && localDayDiff(body.start_day, body.end_day) < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['end_day'],
        message: 'La fecha final no puede ser anterior a la inicial',
      });
    }
    const ids = body.workers.map((w) => w.worker_id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['workers'],
        message: 'Un trabajador aparece más de una vez',
      });
    }
  });
export type ApplyPatternBody = z.infer<typeof ApplyPatternBodySchema>;

/** Query de `POST /schedule-pattern-assignments`. */
export const ApplyPatternQuerySchema = z.object({
  dry_run: z.stringbool().default(false),
});
export type ApplyPatternQuery = z.infer<typeof ApplyPatternQuerySchema>;

/** `POST /schedule-pattern-assignments/{id}/end`. */
export const EndPatternApplicationBodySchema = z.object({ end_day: LocalDateSchema });
export type EndPatternApplicationBody = z.infer<typeof EndPatternApplicationBodySchema>;

export const WorkerSchedulePatternIdParamsSchema = z.object({ id: WorkerSchedulePatternId });
export type WorkerSchedulePatternIdParams = z.infer<typeof WorkerSchedulePatternIdParamsSchema>;

/** `GET /schedule-pattern-assignments?worker_id&site_id&pattern_id&status&cursor&limit`. */
export const PatternApplicationListQuerySchema = CursorQuerySchema.extend({
  worker_id: WorkerId.optional(),
  site_id: SiteId.optional(),
  pattern_id: SchedulePatternId.optional(),
  status: z.enum(['ACTIVE', 'ENDED', 'ALL']).default('ACTIVE'),
});
export type PatternApplicationListQuery = z.infer<typeof PatternApplicationListQuerySchema>;

export const WorkerSchedulePatternSchema = z.object({
  id: WorkerSchedulePatternId,
  worker: ScheduleWorkerRefSchema,
  pattern: z.object({ id: SchedulePatternId, name: z.string() }),
  site: ScheduleSiteRefSchema,
  start_day: LocalDateSchema,
  end_day: LocalDateSchema.nullable(),
  cycle_offset: z.number().int().nonnegative(),
  status: WorkerSchedulePatternStatusSchema,
  generated_until: LocalDateSchema.nullable(),
  end_reason: WorkerSchedulePatternEndReasonSchema.nullable(),
  source: ScheduleSourceSchema,
  external_ref: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type WorkerSchedulePattern = z.infer<typeof WorkerSchedulePatternSchema>;

export const PatternApplicationListResponseSchema = pageSchema(WorkerSchedulePatternSchema);
export type PatternApplicationListResponse = z.infer<typeof PatternApplicationListResponseSchema>;

/** `201` (o `200` con `dry_run`). */
export const ApplyPatternResponseSchema = z.object({
  applications: z.array(WorkerSchedulePatternSchema),
  summary: z.object({
    workers: z.number().int().nonnegative(),
    days_generated: z.number().int().nonnegative(),
    days_skipped_manual: z.number().int().nonnegative(),
    days_skipped_closed: z.number().int().nonnegative(),
    generated_until: LocalDateSchema,
  }),
});
export type ApplyPatternResponse = z.infer<typeof ApplyPatternResponseSchema>;

// ---------------------------------------------------------------------------
// §4 · Cuadro de turnos
// ---------------------------------------------------------------------------

export const GridCellOriginSchema = z.enum(['GRID', 'PATTERN', 'INTEGRATION']);
export type GridCellOrigin = z.infer<typeof GridCellOriginSchema>;

/** Por qué una celda no se puede editar. */
export const GridCellLockSchema = z.enum(['PERIOD_CLOSED', 'INTEGRATION', 'OUTSIDE_EMPLOYMENT']);
export type GridCellLock = z.infer<typeof GridCellLockSchema>;

/** `GET /schedule-grid?from_day&to_day&site_id&worker_id&search&cursor&limit` (≤ 42 días). */
export const ScheduleGridQuerySchema = CursorQuerySchema.extend({
  from_day: LocalDateSchema,
  to_day: LocalDateSchema,
  site_id: SiteId.optional(),
  worker_id: WorkerId.optional(),
  search: z.string().trim().min(1).max(120).optional(),
}).superRefine(refineDayRange(SCHEDULE_GRID_MAX_DAYS));
export type ScheduleGridQuery = z.infer<typeof ScheduleGridQuerySchema>;

export const GridCellSchema = z.object({
  day: LocalDateSchema,
  assignment_id: ShiftAssignmentId.nullable(),
  /** `null` = nunca asignado. */
  kind: DayPlanKindSchema.nullable(),
  site: ScheduleSiteRefSchema.nullable(),
  shifts: z.array(ShiftSummarySchema),
  origin: GridCellOriginSchema.nullable(),
  holiday: z.object({ id: HolidayId, name: z.string() }).nullable(),
  absence: z.object({ id: AbsenceId, type: AbsenceTypeSchema, partial: z.boolean() }).nullable(),
  locked: GridCellLockSchema.nullable(),
});
export type GridCell = z.infer<typeof GridCellSchema>;

export const ScheduleGridDaySchema = z.object({
  day: LocalDateSchema,
  /** ISO: 1 = lunes … 7 = domingo. */
  weekday: z.number().int().min(1).max(7),
  holiday_names: z.array(z.string()),
});
export type ScheduleGridDay = z.infer<typeof ScheduleGridDaySchema>;

export const ScheduleGridResponseSchema = z.object({
  from_day: LocalDateSchema,
  to_day: LocalDateSchema,
  days: z.array(ScheduleGridDaySchema),
  rows: z.array(
    z.object({
      worker: z.object({
        id: WorkerId,
        name: z.string(),
        internal_code: z.string().nullable(),
        primary_site_id: SiteId.nullable(),
      }),
      active_pattern: z.object({ id: SchedulePatternId, name: z.string() }).nullable(),
      cells: z.array(GridCellSchema),
    }),
  ),
  next_cursor: z.string().nullable(),
  server_time: IsoDateTimeSchema,
});
export type ScheduleGridResponse = z.infer<typeof ScheduleGridResponseSchema>;

/** Una celda a guardar: `SHIFT` con 1–2 plantillas; `REST`/`OFF` sin plantillas. */
export const SaveGridCellSchema = z
  .object({
    worker_id: WorkerId,
    day: LocalDateSchema,
    kind: DayPlanKindSchema,
    site_id: SiteId.optional(),
    template_ids: z.array(ShiftTemplateId).max(2),
    /** La asignación vigente que vio el cliente (`null` si la celda estaba vacía). */
    base_assignment_id: ShiftAssignmentId.nullable(),
  })
  .superRefine((cell, ctx) => {
    if (cell.kind === 'SHIFT' && cell.template_ids.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['template_ids'], message: 'Elige al menos un turno' });
    }
    if (cell.kind !== 'SHIFT' && cell.template_ids.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['template_ids'],
        message: 'Un día sin turno no lleva plantillas',
      });
    }
    if (new Set(cell.template_ids).size !== cell.template_ids.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['template_ids'],
        message: 'No repitas el mismo turno en un día',
      });
    }
  });
export type SaveGridCell = z.infer<typeof SaveGridCellSchema>;

/** `POST /schedule-grid/cells`: 1–500 celdas, una sola transacción (todo o nada). */
export const SaveGridCellsBodySchema = z
  .object({ cells: z.array(SaveGridCellSchema).min(1).max(SCHEDULE_GRID_MAX_CELLS) })
  .superRefine((body, ctx) => {
    const seen = new Set<string>();
    body.cells.forEach((cell, index) => {
      const key = `${cell.worker_id}|${cell.day}`;
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['cells', index, 'day'],
          message: 'La celda aparece más de una vez',
        });
      }
      seen.add(key);
    });
  });
export type SaveGridCellsBody = z.infer<typeof SaveGridCellsBodySchema>;

export const SaveGridCellsResponseSchema = z.object({
  saved: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  cells: z.array(GridCellSchema),
});
export type SaveGridCellsResponse = z.infer<typeof SaveGridCellsResponseSchema>;

/** `GET /schedule-grid/history?worker_id&day`. */
export const GridHistoryQuerySchema = z.object({ worker_id: WorkerId, day: LocalDateSchema });
export type GridHistoryQuery = z.infer<typeof GridHistoryQuerySchema>;

export const GridCellVersionSchema = z.object({
  id: ShiftAssignmentId,
  kind: DayPlanKindSchema,
  site: ScheduleSiteRefSchema.nullable(),
  shifts: z.array(ShiftSummarySchema),
  origin: GridCellOriginSchema,
  created_at: IsoDateTimeSchema,
  created_by: ScheduleAdminRefSchema.nullable(),
  superseded_at: NullableIsoDateTimeSchema,
});
export type GridCellVersion = z.infer<typeof GridCellVersionSchema>;

/** Descendente por `created_at`. */
export const GridHistoryResponseSchema = z.object({ versions: z.array(GridCellVersionSchema) });
export type GridHistoryResponse = z.infer<typeof GridHistoryResponseSchema>;

// ---------------------------------------------------------------------------
// §5 · Feriados
// ---------------------------------------------------------------------------

export const HolidayStatusSchema = z.enum(['ACTIVE', 'REMOVED']);
export type HolidayStatus = z.infer<typeof HolidayStatusSchema>;

/** `GET /holidays?year&site_id&include_removed`. */
export const HolidayListQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  site_id: SiteId.optional(),
  include_removed: z.stringbool().default(false),
});
export type HolidayListQuery = z.infer<typeof HolidayListQuerySchema>;

/** `POST /holidays`. `site_id = null` ⇒ feriado de toda la empresa. */
export const CreateHolidayBodySchema = z.object({
  day: LocalDateSchema,
  name: z.string().trim().min(1).max(80),
  site_id: SiteId.nullable(),
});
export type CreateHolidayBody = z.infer<typeof CreateHolidayBodySchema>;

export const HolidayIdParamsSchema = z.object({ id: HolidayId });
export type HolidayIdParams = z.infer<typeof HolidayIdParamsSchema>;

export const HolidaySchema = z.object({
  id: HolidayId,
  day: LocalDateSchema,
  name: z.string(),
  site: ScheduleSiteRefSchema.nullable(),
  status: HolidayStatusSchema,
  source: ScheduleSourceSchema,
  external_ref: z.string().nullable(),
  created_at: IsoDateTimeSchema,
  removed_at: NullableIsoDateTimeSchema,
});
export type Holiday = z.infer<typeof HolidaySchema>;

export const HolidayListResponseSchema = z.object({ items: z.array(HolidaySchema) });
export type HolidayListResponse = z.infer<typeof HolidayListResponseSchema>;
